import React, { useEffect, useState } from "react";
import PanelDashboardLayout from "../PanelDashboardLayout";
import {
  getPromotionPoliciesViaAdmin,
  updatePromotionPolicyViaAdmin,
} from "../../../utils/Api";
import { toast } from "react-toastify";
import DataTable from "react-data-table-component";
import { useForm } from "react-hook-form";
import CreateIcon from "@mui/icons-material/Create";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";

const PromotionPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [activePolicy, setActivePolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    setLoading(true);
    try {
      const response = await getPromotionPoliciesViaAdmin();
      setPolicies(response?.policies || []);
    } catch (error) {
      console.error("Error fetching policies:", error);
      toast.error(error?.response?.data?.error || "Error fetching promotion policies");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (policy) => {
    setActivePolicy(policy);
    reset({
      maxCarryForward: policy.maxCarryForward ?? 0,
      verificationStatus: policy.verificationStatus || "UNVERIFIED",
      notes: policy.notes || "",
      requireAllPriorYearsCleared: !!policy.requireAllPriorYearsCleared,
      finalYearRequiresZeroBacklog: !!policy.finalYearRequiresZeroBacklog,
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setActivePolicy(null);
  };

  const onSubmit = async (data) => {
    if (!activePolicy) return;
    setSaving(true);
    try {
      const payload = {
        maxCarryForward: Number(data.maxCarryForward),
        verificationStatus: data.verificationStatus,
        notes: data.notes,
        requireAllPriorYearsCleared: !!data.requireAllPriorYearsCleared,
        finalYearRequiresZeroBacklog: !!data.finalYearRequiresZeroBacklog,
      };
      await updatePromotionPolicyViaAdmin(activePolicy._id, payload);
      toast.success("Promotion policy updated!");
      closeEditModal();
      fetchPolicies();
    } catch (error) {
      console.error("Error updating policy:", error);
      toast.error(error?.response?.data?.error || "Error updating promotion policy");
    } finally {
      setSaving(false);
    }
  };

  const tableCustomStyles = {
    headRow: {
      style: {
        color: "#fff",
        backgroundColor: "#0F1015",
      },
    },
  };

  const verificationChip = (status) =>
    status === "VERIFIED" ? (
      <Chip label="VERIFIED" size="small" sx={{ backgroundColor: "#d4edda", color: "#155724" }} />
    ) : (
      <Chip label="UNVERIFIED" size="small" sx={{ backgroundColor: "#f8d7da", color: "#721c24" }} />
    );

  const columns = [
    {
      name: "SL. No",
      selector: (row, index) => ++index,
      width: "70px",
    },
    {
      name: "Program",
      selector: (row) => row?.program?.name || "-",
      sortable: true,
      width: "180px",
    },
    {
      name: "Max Carry Forward",
      selector: (row) => row?.maxCarryForward ?? "-",
      sortable: true,
      width: "160px",
    },
    {
      name: "Evaluation Scope",
      selector: (row) => row?.evaluationScope || "-",
      sortable: true,
      width: "170px",
    },
    {
      name: "Final Year Zero Backlog",
      selector: (row) => (row?.finalYearRequiresZeroBacklog ? "Yes" : "No"),
      width: "180px",
    },
    {
      name: "Verification",
      cell: (row) => verificationChip(row?.verificationStatus),
      width: "150px",
    },
    {
      name: "Actions",
      cell: (row) => (
        <button
          className="text-warning bg-none outline-none border-none"
          title="Edit Policy"
          onClick={() => openEditModal(row)}
        >
          <CreateIcon />
        </button>
      ),
      width: "100px",
    },
  ];

  return (
    <PanelDashboardLayout>
      <div>
        <div className="col-lg-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              <h4 className="card-title">Promotion Policies</h4>
              <p className="card-description">
                Configure the carry-forward caps and verification status used
                when promoting cohorts.
              </p>
              <div className="table-responsive">
                <DataTable
                  columns={columns}
                  data={policies}
                  customStyles={tableCustomStyles}
                  pagination
                  progressPending={loading}
                  noDataComponent={<div className="p-4">No promotion policies found.</div>}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Edit Policy Dialog */}
        <Dialog open={showEditModal} onClose={closeEditModal} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <CreateIcon color="primary" />
              <span>
                Edit Policy{activePolicy?.program?.name ? ` — ${activePolicy.program.name}` : ""}
              </span>
            </Box>
          </DialogTitle>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent dividers>
              <div className="form-group">
                <label>Max Carry Forward</label>
                <input
                  className="form-control p_input"
                  type="number"
                  min={0}
                  {...register("maxCarryForward", { required: true, min: 0 })}
                  aria-invalid={errors.maxCarryForward ? "true" : "false"}
                />
                {errors.maxCarryForward && (
                  <p className="text-danger">A valid max carry forward is required</p>
                )}
              </div>

              <div className="form-group">
                <label>Verification Status</label>
                <select className="form-control p_input" {...register("verificationStatus")}>
                  <option value="UNVERIFIED">UNVERIFIED</option>
                  <option value="VERIFIED">VERIFIED</option>
                </select>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  className="form-control p_input"
                  rows={3}
                  placeholder="Optional notes about this policy"
                  {...register("notes")}
                />
              </div>

              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="requireAllPriorYearsCleared"
                  {...register("requireAllPriorYearsCleared")}
                />
                <label className="form-check-label" htmlFor="requireAllPriorYearsCleared">
                  Require all prior years cleared
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="finalYearRequiresZeroBacklog"
                  {...register("finalYearRequiresZeroBacklog")}
                />
                <label className="form-check-label" htmlFor="finalYearRequiresZeroBacklog">
                  Final year requires zero backlog
                </label>
              </div>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={closeEditModal} color="inherit" type="button">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="success"
                disabled={saving}
                startIcon={saving && <CircularProgress size={16} color="inherit" />}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </div>
    </PanelDashboardLayout>
  );
};

export default PromotionPolicies;
